import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { renderSocialDock } from "@/render/social";

gsap.registerPlugin(ScrollTrigger);

const HIDDEN_Y_PERCENT = 160;

export class SocialDockController {
  readonly #host: HTMLElement;
  #dock?: HTMLElement;
  #footerTrigger?: ScrollTrigger;
  #motion?: gsap.core.Timeline;
  #mounted = false;
  #entered = false;
  #atFooter = false;
  #visibleTarget = false;

  constructor(host: HTMLElement) {
    this.#host = host;
  }

  mount(): void {
    if (this.#mounted) return;
    if (!this.#host.querySelector("[data-social-dock]")) this.#host.innerHTML = renderSocialDock();
    this.#dock = this.#host.querySelector<HTMLElement>("[data-social-dock]") ?? undefined;
    if (!this.#dock) throw new Error("Social dock markup is missing");
    gsap.set(this.#dock, { autoAlpha: 0, yPercent: HIDDEN_Y_PERCENT });
    this.#dock.inert = true;
    this.#dock.setAttribute("aria-hidden", "true");
    this.#mounted = true;
  }

  enter(): void {
    if (!this.#mounted || this.#entered) return;
    this.#entered = true;
    this.#setVisible(!this.#atFooter, true);
  }

  bindFooter(footer: HTMLElement | null): void {
    this.#footerTrigger?.kill();
    this.#footerTrigger = undefined;
    this.#atFooter = footer ? this.#isInViewport(footer) : false;
    if (this.#entered) this.#setVisible(!this.#atFooter);
    if (!footer) return;

    this.#footerTrigger = ScrollTrigger.create({
      trigger: footer,
      start: "top bottom",
      end: "bottom top",
      id: "social-dock-footer-handoff",
      onEnter: () => this.#setAtFooter(true),
      onEnterBack: () => this.#setAtFooter(true),
      onLeave: () => this.#setAtFooter(false),
      onLeaveBack: () => this.#setAtFooter(false),
      onRefresh: (trigger) => this.#setAtFooter(trigger.isActive),
    });
    this.#footerTrigger.refresh();
  }

  refresh(): void {
    this.#footerTrigger?.refresh();
  }

  destroy(): void {
    if (!this.#mounted) return;
    this.#footerTrigger?.kill();
    this.#footerTrigger = undefined;
    this.#motion?.kill();
    this.#motion = undefined;
    if (this.#dock) gsap.set(this.#dock, { clearProps: "opacity,transform,visibility,willChange" });
    this.#host.replaceChildren();
    this.#dock = undefined;
    this.#mounted = false;
    this.#entered = false;
    this.#atFooter = false;
    this.#visibleTarget = false;
  }

  #setAtFooter(atFooter: boolean): void {
    if (this.#atFooter === atFooter) return;
    this.#atFooter = atFooter;
    if (this.#entered) this.#setVisible(!atFooter);
  }

  #setVisible(visible: boolean, entrance = false): void {
    const dock = this.#dock;
    if (!dock || (!entrance && this.#visibleTarget === visible)) return;
    this.#visibleTarget = visible;
    this.#motion?.kill();
    dock.inert = !visible;
    dock.setAttribute("aria-hidden", String(!visible));
    dock.dataset.dockState = visible ? "visible" : "hidden";

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      gsap.set(dock, { autoAlpha: visible ? 1 : 0, yPercent: visible ? 0 : HIDDEN_Y_PERCENT });
      return;
    }

    this.#motion = gsap.timeline().to(dock, {
      autoAlpha: visible ? 1 : 0,
      yPercent: visible ? 0 : HIDDEN_Y_PERCENT,
      duration: entrance ? 0.76 : visible ? 0.42 : 0.32,
      ease: visible ? "power3.out" : "power2.inOut",
      overwrite: "auto",
      willChange: "transform,opacity",
      onComplete: () => {
        gsap.set(dock, { clearProps: "willChange" });
      },
    });
  }

  #isInViewport(element: HTMLElement): boolean {
    const bounds = element.getBoundingClientRect();
    return bounds.top < window.innerHeight && bounds.bottom > 0;
  }
}
